import { Content, Editor, useEditor } from '@tiptap/react';
import { Document } from '@tiptap/extension-document';
import { Paragraph } from '@tiptap/extension-paragraph';
import { Text } from '@tiptap/extension-text';
import { Bold } from '@tiptap/extension-bold';
import { Italic } from '@tiptap/extension-italic';
import { Link } from '@tiptap/extension-link';
import { Heading } from '@tiptap/extension-heading';
import { BulletList, OrderedList, ListItem } from '@tiptap/extension-list';
import { HardBreak } from '@tiptap/extension-hard-break';
import { TextFieldProps } from '@mui/material';
import classNames from 'classnames';

import classes from '../styles/rich-text.module.scss';

export type UseTipTapEditorProps = {
  value: Content;
  editable?: boolean;
  onChange?: (value: Content) => void;
  onFocus?: () => void;
  onBlur?: () => void;
} & Pick<TextFieldProps, 'size'>;

type UseTipTapEditorReturn = {
  editor: Editor;
};

export const useTipTapEditor = ({
  value,
  size = 'small',
  editable = true,
  onChange,
  onFocus,
  onBlur,
}: UseTipTapEditorProps): UseTipTapEditorReturn => {
  const editor = useEditor({
    extensions: [
      Document,
      Paragraph,
      Text,
      Bold,
      Italic,
      HardBreak,
      Link.configure({
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      BulletList,
      OrderedList,
      ListItem,
      Heading.configure({
        levels: [1, 2],
      }),
    ],
    editable,
    editorProps: {
      attributes: {
        class: classNames(classes.editorContent, classes[size]),
      },
    },
    content: value || null,
    onUpdate: ({ editor: editorInstance }) => {
      if (editorInstance.isEmpty) {
        onChange?.('');
      } else {
        onChange?.(editorInstance.getHTML());
      }
    },
    onFocus,
    onBlur,
  });

  return { editor };
};
